import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useNotification } from '../contexts/NotificationContext';
import { apiService } from '../services/ApiService';
import Header from './Header';
import '../styles/CadastrarCliente.css';

const CadastrarCliente = () => {
  const { currentUser } = useAuth();
  const { showNotification } = useNotification();
  const navigate = useNavigate();
  
  // Estado para formulário de cliente
  const [clienteData, setClienteData] = useState({
    nome: '',
    email: '',
    telefone: '',
    endereco: '',
    cidade: '',
    estado: '',
    cep: '',
    cnpj: '',
    observacoes: ''
  });
  
  // Estados para controle de edição
  const [modoEdicao, setModoEdicao] = useState(false);
  const [clienteEmEdicao, setClienteEmEdicao] = useState(null);
  
  // Estado para lista de clientes
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Estado para controle de detalhes expandidos
  const [clientesExpandidos, setClientesExpandidos] = useState({});
  
  // Carregar clientes ao iniciar
  useEffect(() => {
    loadClientes();
  }, []);
  
  // Função para carregar clientes
  const loadClientes = async () => {
    try {
      setLoading(true);
      const result = await apiService.getClientes();
      
      
      if (result.success) {
        // Verifica a estrutura da resposta e extrai os clientes corretamente
        let clientesData = [];
        
        if (result.data) {
          if (Array.isArray(result.data)) {
            // Se data for diretamente um array
            clientesData = result.data;
          } else if (result.data.clientes && Array.isArray(result.data.clientes)) {
            // Se data.clientes for um array
            clientesData = result.data.clientes;
          } else if (Object.keys(result.data).length > 0) {
            
            const possiveisClientes = Object.values(result.data).filter(item => 
              item && typeof item === 'object' && item.nome && item.id
            );
            
            if (possiveisClientes.length > 0) {
              clientesData = possiveisClientes;
            } else {
              // Tentar iterar por todas as propriedades procurando arrays
              for (const key of Object.keys(result.data)) {
                if (Array.isArray(result.data[key])) {
                  if (result.data[key].length > 0 && result.data[key][0].nome) {
                    clientesData = result.data[key];
                    break;
                  }
                }
              }
              
              if (clientesData.length === 0) {
                // Se não conseguimos identificar clientes no objeto data
                console.warn('Estrutura de dados não esperada na resposta:', JSON.stringify(result.data));
              }
            }
          }
        }
        
        setClientes(clientesData);
        
        if (clientesData.length === 0) {
        } else {
          // Atualizar cache local se encontramos clientes
          localStorage.setItem('cached_clientes', JSON.stringify(clientesData));
        }
      } else {
        console.error('Erro ao carregar clientes:', 
          result.error || 'Erro desconhecido', 
          'Resposta completa:', result
        );
        showNotification('error', 'Não foi possível carregar os clientes');
        setClientes([]);
      }
    } catch (error) {
      console.error('Erro ao carregar clientes:', error);
      showNotification('error', 'Erro ao buscar dados de clientes');
      setClientes([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Função para cadastrar ou atualizar cliente
  const handleSalvarCliente = async (e) => {
    e.preventDefault();
    
    // Validar campos obrigatórios
    if (!clienteData.nome.trim()) {
      showNotification('error', 'O nome do cliente é obrigatório');
      return;
    }
    
    try {
      let result;
      
      if (modoEdicao && clienteEmEdicao) {
        // Atualizar cliente existente
        result = await apiService.atualizarCliente(clienteEmEdicao, clienteData);
        
        if (result.success) {
          showNotification('success', 'Cliente atualizado com sucesso!');
          // Limpar formulário e estado de edição
          setClienteData({
            nome: '',
            email: '',
            telefone: '',
            endereco: '',
            cidade: '',
            estado: '',
            cep: '',
            cnpj: '',
            observacoes: ''
          });
          setModoEdicao(false);
          setClienteEmEdicao(null);
          // Recarregar lista de clientes
          loadClientes();
        } else {
          showNotification('error', result.error || 'Erro ao atualizar cliente');
        }
      } else {
        // Cadastrar novo cliente
        result = await apiService.cadastrarCliente(clienteData);
        
        if (result.success) {
          showNotification('success', 'Cliente cadastrado com sucesso!');
          // Limpar formulário
          setClienteData({
            nome: '',
            email: '',
            telefone: '',
            endereco: '',
            cidade: '',
            estado: '',
            cep: '',
            cnpj: '',
            observacoes: ''
          });
          // Recarregar lista de clientes
          loadClientes();
        } else {
          showNotification('error', result.error || 'Erro ao cadastrar cliente');
        }
      }
    } catch (error) {
      console.error('Erro ao salvar cliente:', error);
      showNotification('error', 'Erro ao processar operação de cliente');
    }
  };
  
  // Função para excluir cliente
  const handleExcluirCliente = async (cliente) => {
    if (window.confirm(`Tem certeza que deseja excluir o cliente ${cliente.nome}?`)) {
      try {
        const result = await apiService.excluirCliente(cliente.id);
        
        if (result.success) {
          showNotification('success', 'Cliente excluído com sucesso!');
          
          // Se o cliente excluído estava em edição, limpar o formulário
          if (modoEdicao && clienteEmEdicao === cliente.id) {
            cancelarEdicao();
          }
          
          loadClientes();
        } else {
          showNotification('error', result.error || 'Erro ao excluir cliente');
        }
      } catch (error) {
        console.error('Erro ao excluir cliente:', error);
        showNotification('error', 'Erro ao excluir cliente');
      }
    }
  };
  
  // Função para iniciar edição de cliente
  const handleEditarCliente = (cliente) => {
    setClienteData({
      nome: cliente.nome || '',
      email: cliente.email || '',
      telefone: cliente.telefone || '',
      endereco: cliente.endereco || '',
      cidade: cliente.cidade || '',
      estado: cliente.estado || '',
      cep: cliente.cep || '',
      cnpj: cliente.cnpj || '',
      observacoes: cliente.observacoes || ''
    });
    
    setModoEdicao(true);
    setClienteEmEdicao(cliente.id);
    
    // Rolar para o topo para exibir o formulário
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  // Função para cancelar edição
  const cancelarEdicao = () => {
    setClienteData({
      nome: '',
      email: '',
      telefone: '',
      endereco: '',
      cidade: '',
      estado: '',
      cep: '',
      cnpj: '',
      observacoes: ''
    });
    
    setModoEdicao(false);
    setClienteEmEdicao(null);
  };
  
  // Função para alternar visualização expandida de um cliente
  const toggleClienteExpandido = (clienteId) => {
    setClientesExpandidos(prev => ({
      ...prev,
      [clienteId]: !prev[clienteId]
    }));
  };
  
  // Filtrar clientes com base no termo de busca
  const filteredClientes = clientes.filter(cliente => 
    cliente.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cliente.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cliente.telefone?.includes(searchTerm) ||
    cliente.cnpj?.includes(searchTerm) ||
    cliente.cidade?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    cliente.endereco?.toLowerCase().includes(searchTerm.toLowerCase())
  );
  
  // Função para formatar data
  const formatarData = (dataString) => {
    if (!dataString) return '';
    const data = new Date(dataString);
    return data.toLocaleDateString('pt-BR');
  };
  
  return (
    <div className="cadastrar-cliente-container">
      <Header 
        onNavigateToHome={() => navigate('/home')}
        onNavigateToOrcamentos={() => navigate('/orcamentos')}
        onNavigateToDiarioObra={() => navigate('/diario-obra')}
      />
      
      <div className="cadastrar-cliente-content">
        <div className="cadastrar-cliente-header">
          <h2>{modoEdicao ? 'Editar Cliente' : 'Cadastro de Clientes'}</h2>
          <p>{modoEdicao ? 'Atualize os dados do cliente' : 'Gerencie os dados dos seus clientes'}</p>
        </div>
        
        <div className="cadastrar-cliente-grid">
          {/* Formulário de cadastro/edição */}
          <div className="cliente-form-container">
            <h3>{modoEdicao ? 'Editar Cliente' : 'Novo Cliente'}</h3>
            <form onSubmit={handleSalvarCliente} className="cliente-form">
              <div className="form-group">
                <label htmlFor="nome">Nome*</label>
                <input
                  type="text"
                  id="nome"
                  value={clienteData.nome}
                  onChange={(e) => setClienteData({...clienteData, nome: e.target.value})}
                  placeholder="Nome do cliente"
                  required
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="email">E-mail</label>
                  <input
                    type="email"
                    id="email"
                    value={clienteData.email}
                    onChange={(e) => setClienteData({...clienteData, email: e.target.value})}
                    placeholder="E-mail do cliente"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="telefone">Telefone</label>
                  <input
                    type="tel"
                    id="telefone"
                    value={clienteData.telefone}
                    onChange={(e) => setClienteData({...clienteData, telefone: e.target.value})}
                    placeholder="Telefone do cliente"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="endereco">Endereço</label>
                <input
                  type="text"
                  id="endereco"
                  value={clienteData.endereco}
                  onChange={(e) => setClienteData({...clienteData, endereco: e.target.value})}
                  placeholder="Endereço completo"
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="cidade">Cidade</label>
                  <input
                    type="text"
                    id="cidade"
                    value={clienteData.cidade}
                    onChange={(e) => setClienteData({...clienteData, cidade: e.target.value})}
                    placeholder="Cidade"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="estado">Estado</label>
                  <input
                    type="text"
                    id="estado"
                    value={clienteData.estado}
                    onChange={(e) => setClienteData({...clienteData, estado: e.target.value})}
                    placeholder="Estado"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="cep">CEP</label>
                  <input
                    type="text"
                    id="cep"
                    value={clienteData.cep}
                    onChange={(e) => setClienteData({...clienteData, cep: e.target.value})}
                    placeholder="CEP"
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="cnpj">CNPJ</label>
                <input
                  type="text"
                  id="cnpj"
                  value={clienteData.cnpj}
                  onChange={(e) => setClienteData({...clienteData, cnpj: e.target.value})}
                  placeholder="CNPJ"
                />
              </div>
              
              <div className="form-group">
                <label htmlFor="observacoes">Observações</label>
                <textarea
                  id="observacoes"
                  value={clienteData.observacoes}
                  onChange={(e) => setClienteData({...clienteData, observacoes: e.target.value})}
                  placeholder="Observações sobre o cliente"
                  rows="3"
                ></textarea>
              </div>
              
              <div className="form-buttons">
                <button type="submit" className="btn-cadastrar-cliente">
                  <i className="fas fa-save"></i> {modoEdicao ? 'Salvar Alterações' : 'Cadastrar Cliente'}
                </button>
                
                {modoEdicao && (
                  <button 
                    type="button" 
                    className="btn-cancelar" 
                    onClick={cancelarEdicao}
                  >
                    <i className="fas fa-times"></i> Cancelar
                  </button>
                )}
              </div>
            </form>
          </div>
          
          {/* Lista de clientes */}
          <div className="clientes-list-container">
            <div className="clientes-list-header">
              <h3>Clientes Cadastrados</h3>
              <div className="search-container">
                <input
                  type="text"
                  placeholder="Buscar clientes..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="search-input"
                />
                <i className="fas fa-search search-icon"></i>
              </div>
            </div>
            
            {loading ? (
              <div className="loading-indicator">
                <i className="fas fa-spinner fa-spin"></i> Carregando clientes...
              </div>
            ) : filteredClientes.length > 0 ? (
              <div className="clientes-list">
                {filteredClientes.map(cliente => (
                  <div key={cliente.id} className="cliente-card">
                    <div className="cliente-info">
                      <div className="cliente-header">
                        <h4>{cliente.nome}</h4>
                        <button 
                          className="btn-expandir" 
                          onClick={() => toggleClienteExpandido(cliente.id)}
                          title={clientesExpandidos[cliente.id] ? "Mostrar menos" : "Mostrar mais"}
                        >
                          <i className={`fas fa-chevron-${clientesExpandidos[cliente.id] ? 'up' : 'down'}`}></i>
                        </button>
                      </div>
                      
                      <div className="cliente-dados-basicos">
                        {cliente.email && <p><i className="fas fa-envelope"></i> {cliente.email}</p>}
                        {cliente.telefone && <p><i className="fas fa-phone"></i> {cliente.telefone}</p>}
                        {cliente.cidade && <p><i className="fas fa-map-marker-alt"></i> {cliente.cidade}{cliente.estado ? `, ${cliente.estado}` : ''}</p>}
                      </div>
                      
                      {clientesExpandidos[cliente.id] && (
                        <div className="cliente-detalhes-expandidos">
                          {cliente.cnpj && (
                            <div className="detalhe-item">
                              <span className="detalhe-label"><i className="fas fa-id-card"></i> CNPJ:</span>
                              <span className="detalhe-valor">{cliente.cnpj}</span>
                            </div>
                          )}
                          
                          {cliente.endereco && (
                            <div className="detalhe-item">
                              <span className="detalhe-label"><i className="fas fa-home"></i> Endereço:</span>
                              <span className="detalhe-valor">{cliente.endereco}</span>
                            </div>
                          )}
                          
                          {cliente.cep && (
                            <div className="detalhe-item">
                              <span className="detalhe-label"><i className="fas fa-map-pin"></i> CEP:</span>
                              <span className="detalhe-valor">{cliente.cep}</span>
                            </div>
                          )}
                          
                          {cliente.observacoes && (
                            <div className="detalhe-item observacoes">
                              <span className="detalhe-label"><i className="fas fa-sticky-note"></i> Observações:</span>
                              <span className="detalhe-valor">{cliente.observacoes}</span>
                            </div>
                          )}
                          
                          {cliente.createdAt && (
                            <div className="detalhe-item data">
                              <span className="detalhe-label"><i className="fas fa-calendar-plus"></i> Data de cadastro:</span>
                              <span className="detalhe-valor">{formatarData(cliente.createdAt)}</span>
                            </div>
                          )}
                          
                          {cliente.updatedAt && cliente.updatedAt !== cliente.createdAt && (
                            <div className="detalhe-item data">
                              <span className="detalhe-label"><i className="fas fa-calendar-check"></i> Última atualização:</span>
                              <span className="detalhe-valor">{formatarData(cliente.updatedAt)}</span>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                    
                    <div className="cliente-actions">
                      <button 
                        onClick={() => handleEditarCliente(cliente)}
                        className="btn-editar"
                        title="Editar cliente"
                      >
                        <i className="fas fa-edit"></i>
                      </button>
                      <button 
                        onClick={() => handleExcluirCliente(cliente)}
                        className="btn-excluir"
                        title="Excluir cliente"
                      >
                        <i className="fas fa-trash"></i>
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="no-clientes">
                <i className="fas fa-user-slash"></i>
                <p>Nenhum cliente encontrado</p>
                {searchTerm && (
                  <button 
                    onClick={() => setSearchTerm('')}
                    className="btn-clear-search"
                  >
                    Limpar busca
                  </button>
                )}
              </div>
            )}
            
            <div className="clientes-contador">
              {filteredClientes.length > 0 && (
                <p>{filteredClientes.length} cliente{filteredClientes.length !== 1 ? 's' : ''} encontrado{filteredClientes.length !== 1 ? 's' : ''}</p>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CadastrarCliente; 